import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  SegmentedControl,
  Checkbox,
  Select,
  Text,
  Divider,
  ScrollArea
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { getExportColumns, exportToExcel, exportToPDF } from '../services/api';
import { ExportColumns, ExportFilters, ExportRequest, BetSlipStatus, BetType } from '../types/betslip';

interface ExportDialogProps {
  opened: boolean;
  onClose: () => void;
}

export function ExportDialog({ opened, onClose }: ExportDialogProps) {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<'excel' | 'pdf'>('excel');
  const [columns, setColumns] = useState<ExportColumns | null>(null);

  type FormValues = {
    startDate: Date | null;
    endDate: Date | null;
    casino?: string;
    status?: BetSlipStatus;
    betType?: BetType;
  };

  const form = useForm<FormValues>({
    initialValues: {
      startDate: null,
      endDate: null,
      casino: undefined,
      status: undefined,
      betType: undefined
    }
  });

  useEffect(() => {
    if (opened) {
      loadColumns();
    }
  }, [opened]);

  const loadColumns = async () => {
    try {
      const data = await getExportColumns();
      setColumns(data);
    } catch (error) {
      console.error('Error loading columns:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load export columns',
        color: 'red'
      });
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      const filters: ExportFilters = {
        startDate: form.values.startDate || undefined,
        endDate: form.values.endDate || undefined,
        casino: form.values.casino,
        status: form.values.status,
        betType: form.values.betType
      };

      // Prepare selected columns
      const selectedBetSlipColumns = columns?.betSlips.filter(col => col.selected).map(col => col.key) || [];
      const selectedSelectionColumns = columns?.selections.filter(col => col.selected).map(col => col.key) || [];

      // Debug logging
      console.log('Export request:', {
        exportType,
        filters,
        selectedColumns: {
          betSlips: selectedBetSlipColumns,
          selections: selectedSelectionColumns
        },
        columnsState: columns
      });

      const request: ExportRequest = {
        filters,
        selectedColumns: exportType === 'excel' ? {
          betSlips: selectedBetSlipColumns,
          selections: selectedSelectionColumns
        } : undefined
      };

      if (exportType === 'excel') {
        await exportToExcel(request);
      } else {
        await exportToPDF(request);
      }

      notifications.show({
        title: 'Success',
        message: 'Export completed successfully',
        color: 'green'
      });

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to export data',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (type: 'betSlips' | 'selections', key: string) => {
    if (!columns) return;

    setColumns({
      ...columns,
      [type]: columns[type].map(col =>
        col.key === key ? { ...col, selected: !col.selected } : col
      )
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Export Bet Slips"
      size="lg"
    >
      <Stack spacing="md">
        <SegmentedControl
          value={exportType}
          onChange={(value: 'excel' | 'pdf') => setExportType(value)}
          data={[
            { label: 'Excel', value: 'excel' },
            { label: 'PDF', value: 'pdf' }
          ]}
          fullWidth
        />

        <Divider label="Filters" labelPosition="center" />

        <Group grow>
          <DatePickerInput
            label="Start Date"
            valueFormat="YYYY-MM-DD"
            type="default"
            clearable
            {...form.getInputProps('startDate')}
          />
          <DatePickerInput
            label="End Date"
            valueFormat="YYYY-MM-DD"
            type="default"
            clearable
            {...form.getInputProps('endDate')}
          />
        </Group>

        <Group grow>
          <Select
            label="Casino"
            placeholder="Select casino"
            clearable
            {...form.getInputProps('casino')}
            data={[]} // TODO: Add casino options
          />
          <Select
            label="Status"
            placeholder="Select status"
            clearable
            {...form.getInputProps('status')}
            data={[
              { label: 'Pending', value: 'pending' },
              { label: 'Won', value: 'won' },
              { label: 'Lost', value: 'lost' },
              { label: 'Push', value: 'push' },
              { label: 'Cancelled', value: 'cancelled' }
            ]}
          />
          <Select
            label="Bet Type"
            placeholder="Select bet type"
            clearable
            {...form.getInputProps('betType')}
            data={[
              { label: 'Straight', value: 'straight' },
              { label: 'Parlay', value: 'parlay' },
              { label: 'Teaser', value: 'teaser' },
              { label: 'Round Robin', value: 'round_robin' },
              { label: 'Futures', value: 'futures' },
              { label: 'Prop', value: 'prop' }
            ]}
          />
        </Group>

        {exportType === 'excel' && columns && (
          <>
            <Divider label="Select Columns" labelPosition="center" />
            
            <Group grow align="flex-start">
              <Stack>
                <Text weight={500}>Bet Slips</Text>
                <ScrollArea style={{ height: 300 }}>
                  <Stack spacing="xs">
                    {columns.betSlips.map((col) => (
                      <Checkbox
                        key={col.key}
                        label={col.header}
                        checked={col.selected}
                        onChange={() => toggleColumn('betSlips', col.key)}
                      />
                    ))}
                  </Stack>
                </ScrollArea>
              </Stack>

              <Stack>
                <Text weight={500}>Selections</Text>
                <ScrollArea style={{ height: 300 }}>
                  <Stack spacing="xs">
                    {columns.selections.map((col) => (
                      <Checkbox
                        key={col.key}
                        label={col.header}
                        checked={col.selected}
                        onChange={() => toggleColumn('selections', col.key)}
                      />
                    ))}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Group>
          </>
        )}

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button loading={loading} onClick={handleExport}>
            Export
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}